import { TextField, VFFTextFieldAdapter } from '@eventbrite/design-ops-ds';
import { InputField } from '@eventbrite/eds-input-field';
import { ValidationFormField } from '@eventbrite/eds-validation-form-field';
import { gettext } from '@eventbrite/i18n';
import React, { FunctionComponent } from 'react';
import { checkCardImprovementsFeatureGate } from '../../../utils/statsig';
import { ShouldDisplayError } from '../types';
import {
    CARD_EXPIRATION_DATE_FIELD_NAME,
    MARMALADE_MAX_EXPIRATION_DATE_LENGTH,
    MAX_EXPIRATION_DATE_LENGTH,
} from './constants';
import {
    marmaladeNormalizeExpirationDate,
    normalizeExpirationDate,
} from './utils';

interface CardExpirationProps {
    /**
     * An id for the ValidationFormField
     */
    id?: string;
    /**
     * A name for the ValidationFormField
     */
    name?: string;
    /**
     * A label for the ValidationFormField
     */
    label?: string;
    /**
     * A label for the ValidationFormField
     */
    placeholder?: string;
    /**
     * A callback triggered when the input is blurred
     */
    onBlur?: Function;
    /**
     * A function passed to ValidationFormField to determine if errors should be shown
     */
    shouldDisplayError?: ShouldDisplayError;
    /**
     * Whether to show asterisk to indicate field is required
     */
    shouldDisplayRequired?: boolean;
}

export const CardExpirationDateField: FunctionComponent<
    CardExpirationProps
> = ({
    id = 'expiration-date',
    label = gettext('Expiration Date'),
    placeholder = gettext('MM/YY'),
    onBlur,
    shouldDisplayError,
    shouldDisplayRequired,
}) => {
    const _onBlur = (event: any) => {
        onBlur && onBlur(event);
    };

    const isMarmaladeInputEnabled = checkCardImprovementsFeatureGate();

    const normalizer = isMarmaladeInputEnabled
        ? marmaladeNormalizeExpirationDate
        : normalizeExpirationDate;

    const expirationDateInput = isMarmaladeInputEnabled ? (
        <VFFTextFieldAdapter onBlur={_onBlur}>
            <TextField
                autoComplete="cc-exp"
                data-automation="card-expiration-date-field"
                id={id}
                label={label}
                maxLength={MARMALADE_MAX_EXPIRATION_DATE_LENGTH}
                // TODO: Remove the toString() call when the input accepts Lazystrings. https://eventbrite.slack.com/archives/C03MALB8TE3/p1660724336802569
                placeholder={gettext('MM  /  YY').toString()}
                required={shouldDisplayRequired}
                type="text"
            />
        </VFFTextFieldAdapter>
    ) : (
        <InputField
            id={id}
            label={label}
            placeholder={placeholder}
            type="number"
            autoComplete="cc-exp"
            required={shouldDisplayRequired}
            maxLength={MAX_EXPIRATION_DATE_LENGTH}
            data-automation="card-expiration-date-field"
            onBlur={_onBlur}
        />
    );

    return (
        <ValidationFormField
            shouldDisplayError={shouldDisplayError}
            required={shouldDisplayRequired}
            name={CARD_EXPIRATION_DATE_FIELD_NAME}
            normalize={normalizer}
            v2={true}
        >
            {expirationDateInput}
        </ValidationFormField>
    );
};
